import { AnimatePresence, motion, useIsPresent, useReducedMotion } from 'framer-motion';
import { useCallback, useId, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';

import { CloseIcon } from '../lib/icons';
import { Portal } from '../lib/Portal';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import { useFocusTrap } from '../lib/useFocusTrap';
import { useOnEscape } from '../lib/useOnEscape';

import styles from './Modal.module.css';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  className?: string;
  children?: ReactNode;
}

/** Центрированный диалог. Для мобильных потоков предпочтителен Sheet. */
function ModalSurface({
  onClose,
  title,
  className,
  children,
}: Omit<ModalProps, 'open'>): React.ReactElement {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const reduceMotion = useReducedMotion();
  const present = useIsPresent();

  const close = useCallback(() => {
    if (present) onClose();
  }, [onClose, present]);

  useBodyScrollLock(true);
  useOnEscape(true, close);
  useFocusTrap(panelRef, true);

  return (
    <div className={styles.root}>
      <motion.div
        className={styles.backdrop}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: reduceMotion ? 0 : 0.2 }}
        onClick={present ? close : undefined}
      />

      <motion.div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title !== undefined ? titleId : undefined}
        tabIndex={-1}
        className={cn(styles.panel, className)}
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 8 }}
        transition={{ duration: reduceMotion ? 0 : 0.22, ease: [0.22, 1, 0.36, 1] }}
      >
        {title !== undefined ? (
          <div className={styles.header}>
            <h2 className={styles.title} id={titleId}>
              {title}
            </h2>
            <button
              type="button"
              className={styles.close}
              onClick={close}
              aria-label={t('common.close')}
            >
              <CloseIcon size={18} />
            </button>
          </div>
        ) : null}
        <div className={styles.body}>{children}</div>
      </motion.div>
    </div>
  );
}

/** Keep scroll and focus locked until the exit animation has finished. */
export function Modal({ open, ...props }: ModalProps): React.ReactElement {
  return (
    <Portal>
      <AnimatePresence>{open ? <ModalSurface {...props} /> : null}</AnimatePresence>
    </Portal>
  );
}
